import { Amplify } from "aws-amplify";
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut as cognitoSignOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { clearAccessTokenProvider, setAccessTokenProvider } from "../services/api";

function clearAuthParamsFromUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  ["code", "state"].forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  });
  if (changed) {
    const newSearch = url.searchParams.toString();
    const prefix = newSearch ? `?${newSearch}` : "";
    const newUrl = `${url.pathname}${prefix}${url.hash}`;
    window.history.replaceState({}, document.title, newUrl);
  }
}

interface AuthConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
}

interface AuthState {
  username: string;
  email?: string;
}

export type UserRole = "root" | "admin" | "ops" | "consultant";

interface AuthContextValue {
  user: AuthState | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  roles: UserRole[];
  hasRole: (role: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const GROUP_ROLE_MAP: Record<UserRole, string> = {
  root: "root",
  admin: "admin",
  ops: "ops",
  consultant: "consultant",
};

function deriveRoles(groups: string[]): UserRole[] {
  const normalized = groups.map((group) => group.toLowerCase());
  return (Object.keys(GROUP_ROLE_MAP) as UserRole[]).filter((role) => normalized.includes(GROUP_ROLE_MAP[role]));
}

function readConfig(): AuthConfig {
  const region = import.meta.env.VITE_COGNITO_REGION as string | undefined;
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const userPoolWebClientId = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined;
  const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
  const redirectSignIn = import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined;
  const redirectSignOut = import.meta.env.VITE_COGNITO_LOGOUT_URI as string | undefined;

  const missing = Object.entries({
    VITE_COGNITO_REGION: region,
    VITE_COGNITO_USER_POOL_ID: userPoolId,
    VITE_COGNITO_USER_POOL_CLIENT_ID: userPoolWebClientId,
    VITE_COGNITO_DOMAIN: domain,
    VITE_COGNITO_REDIRECT_URI: redirectSignIn,
    VITE_COGNITO_LOGOUT_URI: redirectSignOut,
  })
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(`Missing Cognito env vars: ${missing.join(", ")}`);
  }

  return {
    region,
    userPoolId,
    userPoolWebClientId,
    domain,
    redirectSignIn,
    redirectSignOut,
  } as AuthConfig;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthState | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<UserRole[]>([]);

  const config = useMemo(readConfig, []);
  const origin = typeof window !== "undefined" ? window.location.origin : undefined;

  const redirectSignInUrls = useMemo(() => {
    const urls = new Set<string>([config.redirectSignIn]);
    if (origin) {
      urls.add(origin);
      urls.add(`${origin}/`);
    }
    return Array.from(urls);
  }, [config.redirectSignIn, origin]);

  const redirectSignOutUrls = useMemo(() => {
    const urls = new Set<string>([config.redirectSignOut]);
    if (origin) {
      urls.add(origin);
      urls.add(`${origin}/`);
    }
    return Array.from(urls);
  }, [config.redirectSignOut, origin]);

  useEffect(() => {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: config.domain,
              scopes: ["openid", "email", "profile"],
              redirectSignIn: redirectSignInUrls,
              redirectSignOut: redirectSignOutUrls,
              responseType: "code",
            },
          },
        },
      },
    });

    const loadCurrentUser = async () => {
      try {
        const cognitoUser = await getCurrentUser();
        const session = await fetchAuthSession();
        // If no valid tokens, treat as unauthenticated. This can happen when a stale
        // session exists in localStorage but the refresh token has expired, or when
        // this runs concurrently with an in-progress OAuth code exchange. In both
        // cases we must NOT clear the URL params — Amplify may still need code+state.
        if (!session.tokens?.idToken && !session.tokens?.accessToken) {
          setUser(null);
          setRoles([]);
          return;
        }
        const email = (session.tokens?.idToken?.payload?.email as string | undefined) ?? cognitoUser.signInDetails?.loginId;
        setUser({
          username: cognitoUser.username,
          email,
        });
        const groups = (session.tokens?.idToken?.payload["cognito:groups"] as string[] | undefined) ?? [];
        setRoles(deriveRoles(groups));
        clearAuthParamsFromUrl();
      } catch {
        setUser(null);
        setRoles([]);
        // Do NOT clear auth URL params here — Amplify may still need code+state
        // to complete an in-progress OAuth code exchange.
      }
    };

    const authListener = ({ payload }: { payload: { event: string } }) => {
      // Handle both direct sign-in and the OAuth redirect callback completion.
      if (payload.event === "signIn" || payload.event === "signInWithRedirect") {
        void loadCurrentUser();
      }
      if (payload.event === "signOut") {
        setUser(null);
        setRoles([]);
      }
    };

    const unsubscribe = Hub.listen("auth", authListener);

    void loadCurrentUser().finally(() => setLoading(false));

    setAccessTokenProvider(async () => {
      try {
        let session = await fetchAuthSession();
        // Amplify v6 has a known issue where it may return cached expired tokens
        // for OAuth (hosted UI) flows without auto-refreshing. Check expiry ourselves
        // and force a refresh if the token is expired or within 60 seconds of expiry.
        // Note: the native Cognito User Pools authorizer on REST APIs requires the ID
        // token (not the access token) — it validates the `aud` claim which only ID
        // tokens carry.
        const exp = (session.tokens?.idToken?.payload?.exp ?? 0) as number;
        if (exp > 0 && Date.now() / 1000 > exp - 60) {
          session = await fetchAuthSession({ forceRefresh: true });
        }
        const idToken = session.tokens?.idToken?.toString();
        return idToken ?? null;
      } catch (err) {
        console.warn("Unable to fetch access token", err);
        return null;
      }
    });

    return () => {
      unsubscribe();
      clearAccessTokenProvider();
    };
  }, [config, redirectSignInUrls, redirectSignOutUrls]);

  async function signIn() {
    await signInWithRedirect();
  }

  async function signOut() {
    await cognitoSignOut({ global: true });
    setRoles([]);
  }

  async function getAccessToken(): Promise<string | null> {
    try {
      let session = await fetchAuthSession();
      const exp = (session.tokens?.idToken?.payload?.exp ?? 0) as number;
      if (exp > 0 && Date.now() / 1000 > exp - 60) {
        session = await fetchAuthSession({ forceRefresh: true });
      }
      const idToken = session.tokens?.idToken?.toString();
      return idToken ?? null;
    } catch (err) {
      console.warn("Unable to get access token", err);
      return null;
    }
  }

  function hasRole(role: UserRole): boolean {
    return roles.includes(role);
  }

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signOut,
    getAccessToken,
    roles,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
